import { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';

/* 
Note that the resizing logic won't work if a component above the table conditionally renders.
A possible solution is to allow the consumer to pass in dependencies to trigger recalculating
the table height.
*/

export interface useTableResizerProps {
  tableStartSelector?: string;
  /** Debounce delay on window resize event listener. Set to 0 to disable the listener. */
  windowResizeDelay?: number;
  /**
   * A reference to the table's parent div. The table should be the first child of the wrapper element.
   *
   * Everything inside this wrapper will be within view, giving the table the maximum height it can grow to.
   */
  wrapperRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const useTableResizer = ({
  tableStartSelector = 'thead',
  windowResizeDelay = 500,
  wrapperRef,
}: useTableResizerProps) => {
  const [resizeTrigger, setResizeTrigger] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState<string>(null);
  const [tableHeaderHeight, setTableHeaderHeight] = useState<number>(null);

  useEffect(() => {
    const wrapper = wrapperRef?.current;
    if (!wrapper) return;

    const [table, ...children] = wrapper.children;
    const tableStart = table.querySelector(tableStartSelector);
    const { y: startY = 0, height: startHeight = 0 } =
      tableStart.getBoundingClientRect();
    const childrenHeight = children.reduce(
      (acc, curr) => acc + curr.getBoundingClientRect().height,
      0,
    );

    setTableHeight(`calc(100vh - ${startY + childrenHeight}px)`);
    setTableHeaderHeight(startHeight);
  }, [resizeTrigger, tableStartSelector, wrapperRef]);

  useEffect(() => {
    if (!windowResizeDelay) return;

    const triggerResize = debounce(() => {
      setResizeTrigger(prev => !prev);
    }, windowResizeDelay);

    window.addEventListener('resize', triggerResize);
    return () => {
      window.removeEventListener('resize', triggerResize);
    };
  }, [windowResizeDelay]);

  return { tableHeight, tableHeaderHeight };
};

export default useTableResizer;
